import React, { useCallback, useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    TransactionReviewField,
    TRANSACTION_REVIEW_FIELD_LABELS,
} from "@common/types/transactionFieldReview";
import {
    useReviewableFields,
    withSelectedReviewFields,
} from "@hooks/useReviewableFields";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import { BaseFilterProps } from "../../lib";
import { ModeRadioGroup } from "./ModeRadioGroup";
import {
    ReviewerFilterSection,
    useReverseReviewers,
} from "./ReviewerFilterSection";
import styles from "./ReviewFilter.module.scss";

const toReviewFieldOptions = (
    fields: TransactionReviewField[],
): FilterMultiselectOption[] =>
    fields.map((field) => ({
        value: field,
        label: TRANSACTION_REVIEW_FIELD_LABELS[field],
    }));

const FIELD_MODE_OPTIONS: Array<{
    value: "any" | "every";
    label: string;
}> = [
    { value: "any", label: "Any field" },
    { value: "every", label: "Every field" },
];

export const ReviewFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const reviewableFields = useReviewableFields();

    const reviewedFields = useMemo(
        () => filters.reviewedFields ?? [],
        [filters.reviewedFields],
    );
    const unreviewedFields = useMemo(
        () => filters.unreviewedFields ?? [],
        [filters.unreviewedFields],
    );
    const reviewedFieldOptions = useMemo(
        () =>
            toReviewFieldOptions(
                withSelectedReviewFields({
                    reviewableFields,
                    selectedFields: reviewedFields,
                }),
            ),
        [reviewableFields, reviewedFields],
    );
    const unreviewedFieldOptions = useMemo(
        () =>
            toReviewFieldOptions(
                withSelectedReviewFields({
                    reviewableFields,
                    selectedFields: unreviewedFields,
                }),
            ),
        [reviewableFields, unreviewedFields],
    );
    const reviewerIds = useMemo(
        () => filters.reviewerIds ?? [],
        [filters.reviewerIds],
    );
    const reviewerMode = filters.reviewerMode ?? "any";
    const reviewedFieldsMode = filters.reviewedFieldsMode ?? "any";
    const unreviewedFieldsMode = filters.unreviewedFieldsMode ?? "every";

    const isFilterActive = useMemo(
        () =>
            reviewedFields.length > 0 ||
            unreviewedFields.length > 0 ||
            reviewerIds.length > 0 ||
            filters.reviewedFieldsMode !== undefined ||
            filters.unreviewedFieldsMode !== undefined ||
            filters.reviewerMode !== undefined,
        [
            reviewedFields,
            unreviewedFields,
            reviewerIds,
            filters.reviewedFieldsMode,
            filters.unreviewedFieldsMode,
            filters.reviewerMode,
        ],
    );

    const handleReset = useCallback(() => {
        onChange({
            reviewedFields: undefined,
            reviewedFieldsMode: undefined,
            unreviewedFields: undefined,
            unreviewedFieldsMode: undefined,
            reviewerIds: undefined,
            reviewerMode: undefined,
        });
    }, [onChange]);

    const handleReviewerIdsChange = useCallback(
        (ids: number[] | undefined) => onChange({ reviewerIds: ids }),
        [onChange],
    );

    const reverseReviewers = useReverseReviewers({
        reviewerIds,
        onReviewerIdsChange: handleReviewerIdsChange,
    });

    return (
        <div className={styles.filter}>
            <div className={styles.sectionTitle}>Reviewed</div>
            <ModeRadioGroup
                name="reviewedFieldsMode"
                value={reviewedFieldsMode}
                options={FIELD_MODE_OPTIONS}
                onChange={(value) => onChange({ reviewedFieldsMode: value })}
            />

            <div className={styles.sectionWithTopBorder}>
                <FilterMultiselect
                    inDropdown={false}
                    searchable={false}
                    options={reviewedFieldOptions}
                    value={reviewedFields}
                    onChange={(val: FilterMultiselectValue[]) =>
                        onChange({
                            reviewedFields:
                                val.length > 0
                                    ? (val as TransactionReviewField[])
                                    : undefined,
                            reviewedFieldsMode:
                                val.length === 0
                                    ? undefined
                                    : reviewedFieldsMode,
                        })
                    }
                    name="reviewedFields"
                    onBlur={noop}
                />
            </div>

            <div className={styles.sectionWithTopBorder}>
                <div className={styles.sectionTitle}>Unreviewed</div>

                <ModeRadioGroup
                    name="unreviewedFieldsMode"
                    value={unreviewedFieldsMode}
                    options={FIELD_MODE_OPTIONS}
                    onChange={(value) =>
                        onChange({ unreviewedFieldsMode: value })
                    }
                />
            </div>
            <div className={styles.sectionWithTopBorder}>
                <FilterMultiselect
                    inDropdown={false}
                    options={unreviewedFieldOptions}
                    value={unreviewedFields}
                    searchable={false}
                    onChange={(val: FilterMultiselectValue[]) =>
                        onChange({
                            unreviewedFields:
                                val.length > 0
                                    ? (val as TransactionReviewField[])
                                    : undefined,
                            unreviewedFieldsMode:
                                val.length === 0
                                    ? undefined
                                    : unreviewedFieldsMode,
                        })
                    }
                    name="unreviewedFields"
                    onBlur={noop}
                />
            </div>

            <ReviewerFilterSection
                modeName="reviewerMode"
                idsName="reviewerIds"
                reviewerIds={reviewerIds}
                reviewerMode={reviewerMode}
                onModeChange={(mode) => onChange({ reviewerMode: mode })}
                onReviewerIdsChange={handleReviewerIdsChange}
            />
            {isFilterActive && (
                <FilterActionsFooter
                    deselectFunction={handleReset}
                    selectedCount={
                        reviewedFields.length +
                        unreviewedFields.length +
                        reviewerIds.length
                    }
                    reverseFunction={reverseReviewers}
                />
            )}
        </div>
    );
};
